import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError, attachmentUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Paperclip, Pencil, ExternalLink, Loader2, Receipt as ReceiptIcon, Trash2, Edit3 } from "lucide-react";
import AttachReceiptDialog from "@/components/AttachReceiptDialog";
import EditPaymentDialog, { DeletePaymentDialog } from "@/components/EditPaymentDialog";
import { toast } from "sonner";

const METHOD_LABEL = {
    manual: "Cash / manual",
    bank: "Bank transfer",
    card: "Card",
    cheque: "Cheque",
    other: "Other",
};

/**
 * BillPaymentsDialog — show every payment recorded against a single bill,
 * with per-row controls to view / attach / replace / remove the receipt.
 *
 * Reuses GET /api/payments?direction=made and filters client-side to
 * source === "bill" && bill_id === bill.id, so we don't need a dedicated
 * "by-bill" endpoint.
 *
 * Props:
 *   bill              — { id, number, vendor_name, total, balance, amount_paid, currency }
 *                       When null, dialog is closed.
 *   onClose()         — close handler
 *   onChanged()       — fired when a receipt is attached/removed (parent can refetch bills)
 */
export default function BillPaymentsDialog({ bill, onClose, onChanged }) {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [attachTarget, setAttachTarget] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const load = async () => {
        if (!bill) return;
        setLoading(true);
        try {
            const [{ data }, acctRes] = await Promise.all([
                api.get(`/payments?direction=made`),
                api.get(`/bank-accounts`),
            ]);
            const mine = (data.items || []).filter(
                (p) => p.source === "bill" && p.bill_id === bill.id,
            );
            // newest first
            mine.sort((a, b) => (b.paid_on || "").localeCompare(a.paid_on || ""));
            setRows(mine);
            setBankAccounts(acctRes.data?.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (bill) load(); /* eslint-disable-next-line */ }, [bill]);

    const onAttachSaved = async () => {
        setAttachTarget(null);
        await load();
        onChanged?.();
    };

    const onEditSaved = async () => {
        setEditTarget(null);
        await load();
        onChanged?.();
    };

    const onDeleted = async () => {
        setDeleteTarget(null);
        await load();
        onChanged?.();
    };

    return (
        <>
            <Dialog open={!!bill} onOpenChange={(v) => !v && onClose?.()}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                            <ReceiptIcon className="h-5 w-5 text-secondary" />
                            Payments for {bill?.number}
                        </DialogTitle>
                    </DialogHeader>
                    {bill && (
                        <div className="space-y-3">
                            <div className="rounded-2xl bg-sand-100 p-3 text-xs flex items-center justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="text-muted-foreground uppercase tracking-wider">Vendor</div>
                                    <div className="font-medium text-ocean-700 mt-0.5 truncate">{bill.vendor_name || "—"}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-muted-foreground uppercase tracking-wider">Total</div>
                                    <div className="font-mono text-ocean-700 mt-0.5">{fmtMoney(bill.total, bill.currency)}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-muted-foreground uppercase tracking-wider">Paid</div>
                                    <div className="font-mono text-secondary-dark mt-0.5">{fmtMoney(bill.amount_paid || 0, bill.currency)}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-muted-foreground uppercase tracking-wider">Balance</div>
                                    <div className="font-mono text-coral-dark mt-0.5">{fmtMoney(bill.balance || 0, bill.currency)}</div>
                                </div>
                            </div>

                            {loading && (
                                <div className="py-10 text-center text-sm text-muted-foreground inline-flex items-center justify-center gap-2 w-full">
                                    <Loader2 className="h-4 w-4 animate-spin text-secondary" /> Loading payments…
                                </div>
                            )}

                            {!loading && rows.length === 0 && (
                                <div className="py-10 text-center text-sm text-muted-foreground" data-testid="bill-payments-empty">
                                    No payments recorded against this bill yet.
                                </div>
                            )}

                            {!loading && rows.length > 0 && (
                                <div className="rounded-2xl border border-sand-300 divide-y divide-sand-100 overflow-hidden">
                                    {rows.map((p) => (
                                        <div
                                            key={p.id}
                                            className="px-4 py-3 flex items-center gap-3 flex-wrap"
                                            data-testid={`bill-payment-row-${p.id}`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-medium text-ocean-700">
                                                    {fmtMoney(p.amount, p.currency || bill.currency)}
                                                    <span className="text-muted-foreground font-normal"> · {fmtDate(p.paid_on)}</span>
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                                                    <span>{METHOD_LABEL[p.method] || p.method}</span>
                                                    {p.reference && <span>· {p.reference}</span>}
                                                    {p.attachment_id && (
                                                        <Badge className="bg-secondary/15 text-secondary-dark border-0 text-[10px] uppercase tracking-wider">
                                                            <Paperclip className="h-3 w-3 mr-0.5" /> Receipt
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                {p.attachment_id && (
                                                    <a
                                                        href={attachmentUrl(p.attachment_id)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center justify-center h-8 w-8 rounded-md text-secondary-dark hover:bg-secondary/10"
                                                        title="View receipt"
                                                        data-testid={`bill-payment-view-receipt-${p.id}`}
                                                    >
                                                        <ExternalLink className="h-4 w-4" />
                                                    </a>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setAttachTarget({
                                                        ...p,
                                                        party_name: p.party_name || bill.vendor_name,
                                                        invoice_number: p.invoice_number || bill.number,
                                                    })}
                                                    title={p.attachment_id ? "Replace or remove receipt" : "Attach a receipt"}
                                                    data-testid={`bill-payment-attach-${p.id}`}
                                                >
                                                    {p.attachment_id ? (
                                                        <Pencil className="h-4 w-4 text-muted-foreground" />
                                                    ) : (
                                                        <Paperclip className="h-4 w-4 text-muted-foreground" />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setEditTarget({
                                                        ...p,
                                                        party_name: p.party_name || bill.vendor_name,
                                                        invoice_number: p.invoice_number || bill.number,
                                                    })}
                                                    title="Edit payment"
                                                    data-testid={`bill-payment-edit-${p.id}`}
                                                >
                                                    <Edit3 className="h-4 w-4 text-muted-foreground" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setDeleteTarget({
                                                        ...p,
                                                        party_name: p.party_name || bill.vendor_name,
                                                        invoice_number: p.invoice_number || bill.number,
                                                    })}
                                                    title="Delete payment"
                                                    data-testid={`bill-payment-delete-${p.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4 text-coral-dark" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <AttachReceiptDialog
                payment={attachTarget}
                onClose={() => setAttachTarget(null)}
                onSaved={onAttachSaved}
            />

            <EditPaymentDialog
                payment={editTarget}
                bankAccounts={bankAccounts}
                onClose={() => setEditTarget(null)}
                onSaved={onEditSaved}
            />

            <DeletePaymentDialog
                payment={deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onDeleted={onDeleted}
            />
        </>
    );
}
